import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, X, Eye, EyeOff } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = window.__BACKEND_URL;

const CATEGORIES = [
  { value: 'overview', label: 'Overview' },
  { value: 'projects', label: 'Projects' },
  { value: 'channels', label: 'Channels' },
  { value: 'live_ingest', label: 'Live Ingest' },
  { value: 'clip_engine', label: 'Clip Engine' },
  { value: 'storage', label: 'Storage' },
  { value: 'dashboards', label: 'Dashboards' },
  { value: 'automation', label: 'Automation' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'team', label: 'Team' },
  { value: 'settings', label: 'Settings' },
  { value: 'sop', label: 'SOPs' },
  { value: 'troubleshooting', label: 'Troubleshooting' }
];

export default function FounderManualEditor() {
  const { slug } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('overview');
  const [bodyMarkdown, setBodyMarkdown] = useState('');
  const [order, setOrder] = useState(0);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    if (slug) {
      loadPage();
    } else {
      setLoading(false);
    }
  }, [slug, user]);

  const loadPage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/founder-manual/pages/${slug}`,
        { withCredentials: true }
      );
      const pageData = response.data;
      setPage(pageData);
      setTitle(pageData.title);
      setCategory(pageData.category);
      setBodyMarkdown(pageData.body_markdown);
      setOrder(pageData.order);
    } catch (err) {
      console.error('Error loading page:', err);
      toast.error('Failed to load page');
      navigate('/admin/founder-manual');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title || !bodyMarkdown) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/founder-manual/pages/${slug}`,
        {
          title,
          category,
          body_markdown: bodyMarkdown,
          order
        },
        { withCredentials: true }
      );
      toast.success('Page saved successfully');
      navigate(`/admin/founder-manual/${slug}`);
    } catch (err) {
      console.error('Error saving page:', err);
      toast.error(err.response?.data?.detail || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#121417]">
      {/* Header */}
      <div className="p-6 border-b border-gray-800 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Edit Page</h1>
          <p className="text-sm text-gray-400">{slug}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPreview(!preview)}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
          >
            {preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {preview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={() => navigate(`/admin/founder-manual/${slug}`)}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {preview ? (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl font-bold text-white mb-6">{title}</h1>
              <div className="prose prose-invert prose-cyan max-w-none">
                <ReactMarkdown>{bodyMarkdown}</ReactMarkdown>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Page title"
                  className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
                />
              </div>

              {/* Category & Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Order
                  </label>
                  <input
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value))}
                    placeholder="0"
                    className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
                  />
                </div>
              </div>

              {/* Markdown Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-400">
                    Content (Markdown)
                  </label>
                  <a
                    href="https://www.markdownguide.org/cheat-sheet/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-cyan-500 hover:text-cyan-400"
                  >
                    Markdown Guide ↗
                  </a>
                </div>
                <textarea
                  value={bodyMarkdown}
                  onChange={(e) => setBodyMarkdown(e.target.value)}
                  placeholder="Write your content in Markdown..."
                  rows={25}
                  className="w-full px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-cyan-600 resize-none"
                />
              </div>

              {page?.is_system_page && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-yellow-300">
                    ⚠️ This is a system page. Changes will affect the default manual content.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
